'use client';

import { useEffect, useRef, useState } from 'react';
import { PaperObject } from '@/components/piai/items/PaperObject';
import type { PaperKind } from '@/lib/piai/types';

export type PaymentCadence = 'monthly' | 'quarterly' | 'yearly';

export const LANDING_MONTH_ITEMS: {
  day: number;
  kind: PaperKind;
  title: string;
  delay: number;
  attention?: boolean;
  cadence?: PaymentCadence;
  amount?: string;
  when?: string;
}[] = [
  {
    day: 3,
    kind: 'envelope',
    title: 'Internet',
    delay: 0.15,
    cadence: 'monthly',
    amount: '$89',
    when: '3 August',
  },
  {
    day: 9,
    kind: 'house',
    title: 'Council rates',
    delay: 0.45,
    cadence: 'quarterly',
    amount: '~$640',
    when: '9 August',
  },
  { day: 14, kind: 'gift', title: "Maya's birthday", delay: 0.8, when: '14 August' },
  {
    day: 22,
    kind: 'car',
    title: 'Car registration',
    delay: 1.15,
    attention: true,
    cadence: 'yearly',
    amount: '~$1,120',
    when: '22 August',
  },
];

const DAYS = 28;
const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export function LandingMonthPeek({ play: playProp }: { play?: boolean } = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const play = playProp ?? inView;

  useEffect(() => {
    if (playProp != null) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [playProp]);

  const byDay = new Map(LANDING_MONTH_ITEMS.map((item) => [item.day, item]));

  return (
    <div ref={ref} className="piai-mkt-inside-frame" aria-hidden>
      <div className="piai-mkt-inside-surface">
        <div className="piai-mkt-inside-month">August</div>
        <div className="piai-mkt-inside-weekdays">
          {WEEKDAYS.map((d, i) => (
            <span key={`${d}-${i}`}>{d}</span>
          ))}
        </div>
        <div className="piai-mkt-inside-grid">
          {Array.from({ length: DAYS }, (_, i) => {
            const day = i + 1;
            const item = byDay.get(day);
            return (
              <span key={day} className="piai-mkt-inside-day">
                <span className="piai-mkt-inside-num">{day}</span>
                {item && play && (
                  <span
                    className={
                      item.attention
                        ? 'piai-mkt-cal-obj piai-mkt-cal-obj-attention'
                        : 'piai-mkt-cal-obj'
                    }
                    style={{ animationDelay: `${item.delay}s` }}
                    title={
                      item.amount && item.cadence
                        ? `${item.title} · ${item.amount} · ${item.cadence}`
                        : item.title
                    }
                  >
                    <PaperObject kind={item.kind} detail="month" title={item.title} />
                  </span>
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
